"use client";
import {
  __spreadProps,
  __spreadValues
} from "../../../chunk-MMUBH76A.js";
import { jsx } from "react/jsx-runtime";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { OnRampStep, useModalStore } from "../../stores/index.js";
import { useExternalWallets } from "../../providers/ExternalWalletContext.js";
import { useActiveWallet } from "../../hooks/useActiveWallet.js";
import {
  EnabledFlow,
  getOnRampAssets,
  getOnRampNetworks,
  OnRampPurchaseType
} from "@getpara/web-sdk";
import { getNetworkFromChainId, getNetworkOrMainNetEquivalent } from "@getpara/react-common";
const TABS = [
  [EnabledFlow.BUY, "isBuyEnabled", "creditCard", "Buy"],
  [EnabledFlow.RECEIVE, "isReceiveEnabled", "qrCode", "Receive"],
  [EnabledFlow.WITHDRAW, "isWithdrawEnabled", "arrowCircleBrokenDownLeft", "Withdraw"]
];
const DEFAULT = {
  networks: [],
  assets: [],
  isProviderAllowed: {},
  tab: TABS[0][0],
  network: void 0,
  setNetwork: () => {
  },
  asset: void 0,
  setAsset: () => {
  },
  fiatQuantity: "25.00",
  setFiatQuantity: () => {
  },
  onRampConfig: {},
  activeWallet: {}
};
function isValid({ assetInfo }, walletType, network, asset) {
  var _a, _b;
  return !!((_b = (_a = assetInfo[walletType]) == null ? void 0 : _a[network]) == null ? void 0 : _b[asset]);
}
const AddFundsContext = createContext(DEFAULT);
function AddFundsContextProvider({ tab, children }) {
  var _a, _b;
  const onRampConfig = useModalStore((state) => state.onRampConfig);
  const onRampStep = useModalStore((state) => state.onRampStep);
  const setOnRampStep = useModalStore((state) => state.setOnRampStep);
  const { chainId } = useExternalWallets();
  const activeWallet = useActiveWallet();
  const [fiatQuantity, setFiatQuantity] = useState((_b = (_a = onRampConfig.defaultBuyAmount) == null ? void 0 : _a[0]) != null ? _b : "25.00");
  const networks = useMemo(() => {
    const detectedNetwork = getNetworkFromChainId(chainId);
    const isExternal = activeWallet.isExternal && !!detectedNetwork;
    return isExternal ? [getNetworkOrMainNetEquivalent(detectedNetwork, onRampConfig.testMode)] : getOnRampNetworks(onRampConfig.assetInfo, {
      walletType: activeWallet.type,
      allowed: onRampConfig.allowedAssets ? Object.keys(onRampConfig.allowedAssets) : void 0,
      providers: onRampConfig.providers,
      action: OnRampPurchaseType[tab === EnabledFlow.BUY ? "BUY" : "SELL"]
    });
  }, [chainId, activeWallet, onRampConfig, tab]);
  const assets = useMemo(() => {
    return [
      ...new Set(
        Object.values(
          networks.reduce((acc, network2) => {
            var _a2;
            const configValue = (_a2 = onRampConfig.allowedAssets) == null ? void 0 : _a2[network2];
            const allowed = configValue === true ? void 0 : configValue;
            return __spreadProps(__spreadValues({}, acc), {
              [network2]: getOnRampAssets(onRampConfig.assetInfo, {
                walletType: activeWallet.type,
                network: network2,
                allowed,
                providers: onRampConfig.providers,
                action: OnRampPurchaseType[tab === EnabledFlow.BUY ? "BUY" : "SELL"]
              })
            });
          }, {})
        ).flat()
      )
    ];
  }, [networks, onRampConfig, activeWallet, tab]);
  const [network, setNetwork] = useState(
    !!(onRampConfig == null ? void 0 : onRampConfig.defaultOnRampNetwork) && !!onRampConfig.assetInfo[activeWallet.type][onRampConfig.defaultOnRampNetwork] ? onRampConfig.defaultOnRampNetwork : void 0
  );
  const [asset, setAsset] = useState(
    !!network && !!(onRampConfig == null ? void 0 : onRampConfig.defaultOnRampAsset) && assets.includes(onRampConfig.defaultOnRampAsset) ? onRampConfig.defaultOnRampAsset : void 0
  );
  const isProviderAllowed = useMemo(
    () => onRampConfig.providers.reduce(
      (acc, id) => {
        var _a2, _b2, _c, _d, _e;
        return __spreadProps(__spreadValues({}, acc), {
          [id]: !!network && !!asset && !!((_e = (_d = (_c = (_b2 = (_a2 = onRampConfig.assetInfo[activeWallet.type]) == null ? void 0 : _a2[network]) == null ? void 0 : _b2[asset]) == null ? void 0 : _c[id]) == null ? void 0 : _d[1]) == null ? void 0 : _e[tab === EnabledFlow.BUY ? "BUY" : "SELL"])
        });
      },
      {}
    ),
    [onRampConfig, network, asset, activeWallet, tab]
  );
  const value = useMemo(() => {
    if (!onRampConfig || !activeWallet) {
      return DEFAULT;
    }
    return {
      tab,
      networks,
      assets,
      isProviderAllowed,
      asset,
      setAsset,
      network,
      setNetwork,
      fiatQuantity,
      setFiatQuantity,
      activeWallet,
      onRampConfig
    };
  }, [
    tab,
    networks,
    assets,
    isProviderAllowed,
    asset,
    setAsset,
    network,
    setNetwork,
    fiatQuantity,
    setFiatQuantity,
    activeWallet,
    onRampConfig
  ]);
  useEffect(() => {
    if (onRampStep === OnRampStep.PROVIDER && !isValid(onRampConfig, activeWallet.type, network, asset)) {
      setOnRampStep(OnRampStep.SETTINGS);
    }
  }, [onRampStep, onRampConfig, activeWallet.type, network, asset, setOnRampStep]);
  return /* @__PURE__ */ jsx(AddFundsContext.Provider, { value, children });
}
const useAddFunds = () => useContext(AddFundsContext);
export {
  AddFundsContext,
  AddFundsContextProvider,
  TABS,
  useAddFunds
};
